<template>
  <v-select
    ref="addressFinder"
    class="address-autocomplete"
    :clearable="true"
    :clearSearchOnBlur="() => false"
    :filterable="false"
    :getOptionLabel="getLabel"
    :loading="searchLoading"
    :map-keydown="keyHandler"
    :noDrop="!searchInputFocused || addressOptions.length === 0"
    :options="addressOptions"
    :value="value"
    placeholder="Start entering your address"
    @input="optionSelected"
    @open="clickedElement"
    @search="onSearch"
    @search:blur="searchInputFocused = false"
    @search:focus="searchInputFocused = true"
  >
    <template #search="{ attributes, events }">
      <input
        ref="searchInput"
        class="vs__search"
        data-testid="addressSearchInput"
        v-bind="attributes"
        v-on="events"
        v-show="internalValue === null"
      />
    </template>
  </v-select>
</template>

<script>
import axiosInstance from "@/utility/axiosInstance";
import debounce from "lodash.debounce";

export default {
  name: "AddressAutocomplete",
  props: {
    value: {
      type: Object,
    },
  },
  data() {
    return {
      internalValue: null,
      addressOptions: [],
      searchInputFocused: false,
      searchLoading: false,
    };
  },
  watch: {
    value: function(newVal) {
      this.internalValue = newVal;
    },
  },
  created() {
    this.internalValue = this.value;
  },
  methods: {
    clickedElement() {
      if (this.internalValue) {
        this.updateValue(null);
        this.$nextTick(() => {
          this.$refs.searchInput.focus();
        });
      }
    },
    debouncedAddressSearch: debounce(
      (query, id, vm) => vm.doAddressSearch.call(vm, query, id),
      400,
    ),
    doAddressSearch(query, id) {
      const params = {
        query,
      };

      if (id) {
        params["id"] = id;
      }

      this.searchLoading = true;

      axiosInstance
        .get(`/addresses/find`, {
          params,
        })
        .then(({ data }) => {
          this.addressOptions = data;
        })
        .finally(() => {
          this.searchLoading = false;
        });
    },
    getLabel(option) {
      return option.address;
    },
    keyHandler(map, vm) {
      return {
        ...map,
        8: (e) => {
          if (vm.value) {
            e.preventDefault();
            vm.updateValue(null);
            vm.search = "";
          }
        },
      };
    },
    onSearch(query) {
      if (!query.length) {
        this.addressOptions = [];
        return;
      }

      this.debouncedAddressSearch(query, null, this);
    },
    optionSelected(option) {
      if (option === null) {
        // Clear selection
        this.updateValue(option);
        this.$emit("addressSelected", null);
        return;
      }

      this.searchLoading = true;

      axiosInstance
        .get("/addresses/retrieve", {
          params: {
            id: option.id,
          },
        })
        .then(({ data }) => {
          this.updateValue(option);
          this.$store.commit("SET_ADDRESS_ID", option.id);
          this.$emit("addressSelected", data);
        })
        .finally(() => {
          this.searchLoading = false;
        });
    },
    updateValue(value) {
      this.internalValue = value;
      this.$emit("input", value);
    },
  },
};
</script>

<style lang="scss">
.address-autocomplete {
  .vs__dropdown-toggle {
    background-color: $white;
    border: 1px solid $grey-mid;
    border-radius: $border-radius-xs;
    padding: $spacing-small;

    .form-item.--has-error & {
      border-color: $error;
    }
  }

  .vs__search,
  .vs__search:focus {
    font-size: $form-font-size;
    font-weight: $font-regular;
    padding: 0;
    margin: 0;
    border: none;

    &::placeholder {
      color: #707070;
    }
  }

  .vs__open-indicator {
    display: none;
  }

  .vs__actions {
    padding: 0;

    .vs__clear {
      margin: 0;
      width: 20px;
      height: 20px;
      text-align: center;
      text-align: -webkit-center;
      text-align: -moz-center;
    }
  }

  .vs__dropdown-menu {
    border: 1px solid $grey-mid;
    border-top-style: none;
    border-radius: 0 0 $border-radius-xs $border-radius-xs;
    box-shadow: none;
    padding: 0;
  }

  .vs__selected {
    border: none;
    margin: 0;
    padding: 0;
  }
}
</style>
